import {
  isObject, mapValues, pickBy, reduce,
  trim, isUndefined,
} from 'lodash';
import { getDateTime } from '@sda/utils/date';
import {
  string, boolean, number, date,
  nullable, any, object, dateDay,
  dateMinute,
} from './types.bitmasks';
import type { QueryFilterMask } from '@sda/sdk/interfaces';

/**
 * Apply a filter mask to given Map and return a factory of the Map
 * @param  {Partial<T>} data - the object to be processed
 * @param  {QueryFilterMask} mapHelper - the mask map to be used
 * @param  {boolean} [patchOnly] - whether or not to return a partial or the full object. Defaults to full object (false).
 * @return {T} returns the desired map
 */
export const filterMask = <T>(
  data: Partial<T>,
  mapHelper: QueryFilterMask,
  patchOnly = false,
): T => {
  const filtered: any = pickBy(data, (datum, key) => Object.prototype.hasOwnProperty.call(mapHelper, key) && datum !== undefined);

  const mapped: T = mapValues(
    filtered,
    (datum: any, key: string) => {
      const dataType = mapHelper[key];

      if (dataType & nullable && !datum && datum !== false) {
        return null;
      }

      if (dataType & string) {
        return trim(`${datum}`);
      }

      if (dataType & number) {
        return parseFloat(datum);
      }

      if (dataType & date) {
        return getDateTime(datum).toJSDate();
      }

      if (dataType & dateMinute) {
        return getDateTime(datum).startOf('minute').toJSDate();
      }

      if (dataType & dateDay) {
        return getDateTime(datum).startOf('day').toJSDate();
      }

      if (dataType & boolean) {
        return !!datum;
      }

      if (dataType & object) {
        return (!datum || !isObject(datum)) ? {} : pickBy(datum, (property) => !isUndefined(property));
      }

      return datum;
    },
  );

  if (patchOnly) {
    return mapped;
  }

  return reduce(
    mapHelper,
    (results, dataType, key) => {
      if (Object.prototype.hasOwnProperty.call(results, key)) {
        return results;
      }

      let value = null;

      if (dataType & nullable || dataType & any) {
        value = null;
      } else if (dataType & string) {
        value = '';
      } else if (dataType & number) {
        value = 0;
      } else if (dataType & date) {
        value = new Date();
      } else if (dataType & boolean) {
        value = false;
      } else if (dataType & dateDay) {
        value = getDateTime().startOf('day').toJSDate();
      } else if (dataType & dateMinute) {
        value = getDateTime().startOf('minute').toJSDate();
      }

      return {
        ...results,
        [key]: value,
      };
    },
    mapped,
  );
};
